import { useContext, useMemo, useEffect, useRef } from "react";
import { Localized, useLocalization } from "@fluent/react";
import { RadioButtons } from "@narmi/design_system";
import {
  useLoanMinimumPayment,
  useLoanPaymentEnabled,
} from "byzantine/src/dbbl/businessLogic/modules/loanPayment";
import { currencyToCents } from "byzantine/src/dbbl/utils";
import { formAdapters } from "../../../../../src";
import AccountContext from "../../../../contexts/AccountContext";
import LoanPaymentTypesDialog from "../../paydown/LoanPaymentTypesDialog";
import type { LoanPaymentFormType } from "../types";

interface PaymentTypeSectionProps {
  form: LoanPaymentFormType;
}

const PaymentTypeSection = ({ form }: PaymentTypeSectionProps) => {
  const { l10n } = useLocalization();
  const { accounts } = useContext(AccountContext);
  const paymentTypeChoice = useRef("");

  const { minimumPayment, formattedMinimumPayment, formattedDueDate } =
    useLoanMinimumPayment(form.values.toAccountId);

  const { isPrincipalPaymentEnabled, isCustomPaymentEnabled } =
    useLoanPaymentEnabled();

  useEffect(() => {
    if (!form.values.paymentType) {
      return;
    }
    const previousPaymentType = paymentTypeChoice.current;
    paymentTypeChoice.current = form.values.paymentType;

    if (![form.values.paymentType, previousPaymentType].includes("amountDue")) {
      return;
    }

    const paymentAmount =
      form.values.paymentType === "amountDue"
        ? currencyToCents(formattedMinimumPayment)
        : undefined;
    form.setFieldValue("amount", paymentAmount, true);
  }, [form.values.paymentType, formattedMinimumPayment, form]);

  const paymentTypeOptions = useMemo(() => {
    const options: Record<string, string | { value: string; details: string }> =
      {};

    if (minimumPayment > 0) {
      options[l10n.getString("loan-paydown-amount-due")] = {
        value: "amountDue",
        details: l10n.getString("loan-paydown-amount-due-details", {
          amount: formattedMinimumPayment,
          date: formattedDueDate,
        }),
      };
    }

    if (isCustomPaymentEnabled) {
      options[l10n.getString("loan-paydown-custom-amount")] = "custom";
    }

    if (isPrincipalPaymentEnabled) {
      options[l10n.getString("loan-paydown-principal-only")] = "principal";
    }

    return options;
  }, [
    minimumPayment,
    formattedMinimumPayment,
    formattedDueDate,
    isCustomPaymentEnabled,
    isPrincipalPaymentEnabled,
    l10n,
  ]);

  const selectedLoan = useMemo(
    () => accounts.find((a: any) => a.id === form.values.toAccountId),
    [accounts, form.values.toAccountId]
  );

  const isAmortizedLoan = selectedLoan?.isAmortizedLoan?.() || false;

  return (
    <>
      <RadioButtons
        name="paymentType"
        options={paymentTypeOptions}
        {...formAdapters.radio(form, "paymentType")}
        alwaysShowDetails
      />
      <LoanPaymentTypesDialog isAmortizedLoan={isAmortizedLoan} />
      <Localized id="loan-payment-option-disclaimer">
        <span />
      </Localized>
    </>
  );
};

export default PaymentTypeSection;
